<template>
  <div>
    <v-autocomplete
      :value="value.familyId"
      :items="familyOptions"
      :label="$t('components.ModelVersionFields.newVersionOf')"
      :hint="familyHint"
      :disabled="locked"
      persistent-hint
      clearable
      @change="update({ familyId: $event || null })"
    ></v-autocomplete>
    <v-textarea
      :value="value.notes"
      :label="$t('components.ModelVersionFields.versionNotes')"
      :placeholder="$t('components.ModelVersionFields.versionNotesPlaceholder')"
      :disabled="!value.familyId"
      class="mt-4"
      outlined
      rows="3"
      auto-grow
      @input="update({ notes: $event })"
    ></v-textarea>
  </div>
</template>

<script>
import { groupByFamily, latestInFamily, versionFamilyId, versionNumber } from '@/utils/modelVersions.js'

// "New version of" + "Version notes" for an Ad-Hoc model. v-model is { familyId, notes }.
// The rules match the modelVersions-setModelVersion Cloud Function: a version can only be added after the
// latest one of a family, only the latest version can be unlinked, and a model that already has versions of
// its own can't join another family.
export default {
  props: {
    value: {
      type: Object,
      required: true
    },
    // Every Ad-Hoc model (admin/mmmModels).
    models: {
      type: Array,
      required: true
    },
    // The model being linked, or null when it is still being created.
    model: {
      type: Object,
      default: null
    }
  },
  computed: {
    families () {
      return groupByFamily(this.models)
    },
    ownFamilyId () {
      return this.model ? versionFamilyId(this.model) : null
    },
    ownFamily () {
      return this.ownFamilyId ? (this.families.get(this.ownFamilyId) || []) : []
    },
    isVersion () {
      return !!(this.model && this.model.version_family)
    },
    isLatest () {
      return this.ownFamily.length > 0 && latestInFamily(this.ownFamily).id === this.model.id
    },
    // Root of a family that already has later versions.
    hasOwnVersions () {
      return !this.isVersion && this.ownFamily.length > 1
    },
    locked () {
      return this.hasOwnVersions || (this.isVersion && !this.isLatest)
    },
    familyOptions () {
      const options = []
      this.families.forEach((versions, familyId) => {
        // A version only shows its own family; a standalone model shows every other family.
        if (this.isVersion ? familyId !== this.ownFamilyId : familyId === this.ownFamilyId) {
          return
        }
        const latest = latestInFamily(versions)
        options.push({
          value: familyId,
          text: `${latest.name} (v${versionNumber(latest)})`
        })
      })
      return options.sort((a, b) => a.text.localeCompare(b.text))
    },
    familyHint () {
      if (this.hasOwnVersions) {
        return this.$t('components.ModelVersionFields.hasOwnVersions')
      }
      if (this.isVersion) {
        return this.isLatest
          ? this.$t('components.ModelVersionFields.isVersion', { version: versionNumber(this.model) })
          : this.$t('components.ModelVersionFields.notLatest', { version: versionNumber(this.model) })
      }
      if (this.value.familyId && this.families.has(this.value.familyId)) {
        const latest = latestInFamily(this.families.get(this.value.familyId))
        return this.$t('components.ModelVersionFields.willBeVersion', { version: versionNumber(latest) + 1, name: latest.name })
      }
      return this.$t('components.ModelVersionFields.noPreviousVersion')
    }
  },
  methods: {
    update (changes) {
      this.$emit('input', { ...this.value, ...changes })
    }
  }
}
</script>
